import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { WEBSOCKET_URL } from '../../environments/environment';
import { uuid } from '../helpers/uuid.helpers';
import { WSHandshakeMessage } from '../models/WSMessages/WS-handshake-message.model';
import { WSLoadVideoMessage } from '../models/WSMessages/WS-load-video-message.model';
import { getBrowserDeviceInfo } from '../helpers/device-info.helpers';
import { ApiService } from './api-service';
import { Router } from '@angular/router';
import { WSDissociateMessage } from '../models/WSMessages/WS-dissociate-message.model';
import { WSUIConfig } from '../models/WS-ui-config.model';
import { WSFlipBlockMessage } from '../models/WSMessages/WS-flip-block-message.model';
import { WSBroadcastDataMessage } from '../models/WSMessages/WS-broadcast-data-message.model';
import { WSVideoStatusMessage } from '../models/WSMessages/WS-video-status-message.model';
import { VideoStatusType } from '../models/video-status.model';
import { BlockTemplateType } from '../models/block-template.model';
import { NotificationsService } from './notifications-service';
import { WSUser } from '../models/WS-user.model';
import { WSVideoRateMessage } from '../models/WSMessages/WS-video-rate-message.model';
import { TrainingModeType } from '../models/training-mode.model';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TrainingSectionType } from '../models/training-section.model';
import { CoachingIntervalType } from '../models/coaching-interval.model';
import { WSDisplayInformationMessage } from '../models/WSMessages/WS-display-information-message.model';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private readonly apiService: ApiService = inject(ApiService);
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly router: Router = inject(Router);
  private readonly injector: Injector = inject(Injector);
  readonly translateService: TranslateService = inject(TranslateService);

  readonly broadcastData: WritableSignal<WSBroadcastDataMessage | null> =
    signal<WSBroadcastDataMessage | null>(null);
  readonly requestedDisplay: WritableSignal<'map' | 'video'> = signal('video');
  readonly config: WritableSignal<WSUIConfig | null> = signal<WSUIConfig | null>(null);
  readonly contentInformation: WritableSignal<WSLoadVideoMessage | null> =
    signal<WSLoadVideoMessage | null>(null);
  readonly routeCoordinates: WritableSignal<[number, number][]> = signal<[number, number][]>([]);
  readonly socket: Socket = io(WEBSOCKET_URL, { transports: ['websocket'] });
  readonly videoStatus: WritableSignal<VideoStatusType> = signal<VideoStatusType>('pause');
  readonly client: WritableSignal<WSUser | undefined> = signal<WSUser | undefined>(undefined);
  readonly rate: WritableSignal<number> = signal<number>(1);
  readonly hasBegin: Signal<boolean> = computed(() => this.broadcastData() !== null);

  readonly completionPercent: Signal<number> = computed<number>(() => {
    const mode: TrainingModeType = this.contentInformation()?.trainingMode || 'originalSpeed';
    const totalDuration: number = this.apiService.scenicInformation()?.duration || 0;
    const totalDistance: number = this.apiService.scenicInformation()?.distance || 0;
    let value: number = 0;
    switch (mode) {
      case 'variableSpeed':
        value = (this.broadcastData()?.data.distance || 0) / totalDistance;
        break;
      case 'originalSpeed':
        value = (this.broadcastData()?.data.time || 0) / totalDuration;
        break;
      case 'coaching': {
        const intervals: CoachingIntervalType[] = this.apiService.coachingIntervals() ?? [];
        const lastInterval: CoachingIntervalType | undefined = intervals[intervals.length - 1];
        const totalCoachingDuration: number = lastInterval
          ? lastInterval.position + lastInterval.duration
          : totalDuration;
        value = (this.broadcastData()?.data.time || 0) / totalCoachingDuration;
        break;
      }
    }
    return Math.min(Math.floor(value * 100), 100);
  });

  constructor() {
    if (!localStorage.getItem('connectionCode')) localStorage.setItem('connectionCode', uuid(6));
    this.handleWSBasicEvent();
    this.handleScenicLoadFailedEvent();
    this.handleSendConfigEvent();
    this.handleMobileConnectedEvent();
    this.handleMobileDisconnectedEvent();
    this.handleLoadVideoEvent();
    this.handleDisplayInformationEvent();
    this.handleDissociateEvent();
    this.handleFlipBlockEvent();
    this.handleBroadcastDataEvent();
    this.handleSendVideoRateEvent();
    this.handleSendVideoStatusEvent();
    this.handleChangeDisplayEvent();
    this.emitRegisterBrowser();
  }

  private handleWSBasicEvent(): void {
    this.socket.on('connect', () => console.log('Connected'));
    this.socket.on('disconnect', () => this.router.navigate(['/']));
    this.socket.on('error', (error: Error) => console.error('Error', error));
  }

  private handleMobileConnectedEvent(): void {
    this.socket.on('mobileConnected', (message: WSHandshakeMessage) => {
      if (localStorage.getItem('unit') !== message.unit) {
        localStorage.setItem('unit', message.unit);
      }

      const devices: string[] = localStorage.getItem('deviceId')
        ? JSON.parse(localStorage.getItem('deviceId') || '')
        : [];

      if (!devices.includes(message.deviceId)) {
        devices.push(message.deviceId);
        localStorage.setItem('deviceId', JSON.stringify(devices));
      }

      if (message.languageCode && localStorage.getItem('lang') !== message.languageCode) {
        localStorage.setItem('lang', message.languageCode);
        this.translateService.use(message.languageCode);
      }

      this.client.set(message.user);
      this.router.navigate(['/connected']);
    });
  }

  private handleMobileDisconnectedEvent(): void {
    this.socket.on('mobileDisconnected', (): void => {
      this.router.navigate(['/disconnected']);
      return;
    });
  }

  private handleLoadVideoEvent(): void {
    this.socket.on('loadVideo', (message: WSLoadVideoMessage) => {
      this.contentInformation.set(message);
      this.config.set(message.config);
      this.broadcastData.set(null);
      this.videoStatus.set('pause');
      this.apiService.scenicInformation = toSignal(
        this.apiService.getVideoInformation(message.videoId, message.trainingMode),
        {
          initialValue: undefined,
          injector: this.injector,
        },
      );
      this.apiService.scenicTrainingSections =
        message.trainingMode === 'coaching'
          ? signal<TrainingSectionType[]>([])
          : toSignal(this.apiService.getSectionAndCoordinates(message.videoId), {
              initialValue: [],
              injector: this.injector,
            });
      this.apiService.coachingIntervals =
        message.trainingMode === 'coaching'
          ? toSignal(this.apiService.getCoachingIntervals(message.videoId), {
              initialValue: undefined,
              injector: this.injector,
            })
          : signal(undefined);
      const displayRoute: 'coaching' | 'scenic' =
        message.trainingMode === 'coaching' ? 'coaching' : 'scenic';
      this.router.navigate(['/display', displayRoute]);
    });
  }

  private handleScenicLoadFailedEvent(): void {
    effect(() => {
      if (!this.apiService.scenicLoadFailed()) return;
      this.emitVideoLoaded(false);
    });
  }

  private handleSendConfigEvent(): void {
    this.socket.on('sendConfig', (message: WSUIConfig) => {
      this.config.set({ ...this.config(), ...message });
    });
  }

  private handleDisplayInformationEvent(): void {
    this.socket.on('displayInformation', (message: WSDisplayInformationMessage) =>
      this.notificationsService.add(message.type, message.value),
    );
  }

  private handleFlipBlockEvent(): void {
    this.socket.on('flipBlock', (message: WSFlipBlockMessage) => {
      const currentConfig: WSUIConfig | null = this.config();
      if (!currentConfig) return;
      this.config.set({
        ...currentConfig,
        blocksTemplate: currentConfig.blocksTemplate.map((block: BlockTemplateType) =>
          block.type === message.type ? { ...block, isFlip: !block.isFlip } : block,
        ),
      });
    });
  }

  private handleBroadcastDataEvent(): void {
    this.socket.on('broadcastData', (message: WSBroadcastDataMessage) =>
      this.broadcastData.set(message),
    );
  }

  private handleDissociateEvent(): void {
    this.socket.on('dissociate', (message: WSDissociateMessage) => {
      if (message.browserId !== localStorage.getItem('browserId')) return;
      const devices: string[] = JSON.parse(localStorage.getItem('deviceId') || '');
      if (!devices) return;
      if (!devices.includes(message.deviceId)) return;
      const index: number = devices.indexOf(message.deviceId);
      devices.splice(index, 1);
      localStorage.setItem('deviceId', JSON.stringify(devices));
      this.router.navigate(['/']);
    });
  }

  private handleSendVideoRateEvent(): void {
    this.socket.on('sendVideoRate', (message: WSVideoRateMessage) => {
      this.rate.set(message.rate);
    });
  }

  private handleSendVideoStatusEvent(): void {
    this.socket.on('sendVideoStatus', (message: WSVideoStatusMessage) => {
      this.videoStatus.set(message.status);
      if (message.status === 'stop') {
        this.contentInformation.set(null);
        this.broadcastData.set(null);
        this.router.navigate(['/connected']);
      }
    });
  }

  private handleChangeDisplayEvent(): void {
    this.socket.on('changeDisplay', (message: any) => {
      this.requestedDisplay.set(message.type);
    });
  }

  emitRegisterBrowser(): void {
    this.socket.emit(
      'registerBrowser',
      {
        connectionCode: localStorage.getItem('connectionCode'),
        browser: { ...getBrowserDeviceInfo() },
      },
      (data: any) => console.log('Browser registered', data),
    );
  }

  emitVideoLoaded(success: boolean = true): void {
    this.socket.emit('videoLoaded', {
      success,
    });
  }

  emitVideoEnd(): void {
    if (!this.contentInformation()) return;
    this.socket.emit('videoEnd', {
      id: this.contentInformation()!.videoId,
    });
  }

  emitVideoResumed(): void {
    if (!this.contentInformation()) return;
    this.socket.emit('videoResumed', {
      success: true,
    });
  }

  emitTimeChange(time: number): void {
    if (!this.contentInformation()) return;
    this.socket.emit('videoTime', {
      time: Math.round(time),
    });
  }

  disconnect(): void {
    this.socket.disconnect();
  }

  clear(): void {
    this.contentInformation.set(null);
    this.broadcastData.set(null);
    this.videoStatus.set('stop');
    this.rate.set(1);
  }
}
